import { MapPin } from 'lucide-react'
import { type Control, Controller, type FieldErrors, type UseFormWatch } from 'react-hook-form'

import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'
import { type Location } from '@/models/domain/location'

import { LocationsTable } from '../LocationsTable'

interface LocationsFieldProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
  locations: Location[]
  isRootOperator: boolean
  isSysAdmin: boolean
  hasMultipleSystems: boolean
  selectedSystem: string | null
  isLoadingLocationData: boolean
  placeholder: string
}

export const LocationsField = ({
  control,
  watch,
  errors,
  locations,
  isRootOperator,
  isSysAdmin,
  hasMultipleSystems,
  selectedSystem,
  isLoadingLocationData,
  placeholder: _placeholder,
}: LocationsFieldProps) => {
  const isHighLevelRole = isRootOperator || isSysAdmin

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            <MapPin className="w-4 h-4" />
          </span>
          <label
            htmlFor="locations_all"
            className={`text-sm font-medium ${isHighLevelRole ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}
          >
            Locations
          </label>
        </div>
        <LocationsAllCheckbox
          control={control}
          hasMultipleSystems={hasMultipleSystems}
          isHighLevelRole={isHighLevelRole}
        />
      </div>

      <div>
        <Controller
          name="attributes.locations"
          control={control}
          render={({ field }) => {
            const value = Array.isArray(field.value) ? field.value : []
            const locationsAll = watch('attributes.locations_all')
            const isDisabled =
              !selectedSystem || locationsAll || hasMultipleSystems || isHighLevelRole

            return (
              <LocationsTable
                locations={locations}
                selectedLocationIds={value}
                onChange={(newValue) => {
                  if (!hasMultipleSystems && !isHighLevelRole) {
                    field.onChange(newValue)
                  }
                }}
                disabled={isDisabled}
                isLoading={isLoadingLocationData && !!selectedSystem}
                locationsAll={locationsAll || hasMultipleSystems}
              />
            )
          }}
        />
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          Specific locations this user can access {selectedSystem && `(for ${selectedSystem})`}
        </p>
        {errors.attributes?.locations && (
          <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
            {errors.attributes.locations.message}
          </p>
        )}
      </div>
    </div>
  )
}

const LocationsAllCheckbox = ({
  control,
  hasMultipleSystems,
  isHighLevelRole,
}: {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  hasMultipleSystems: boolean
  isHighLevelRole: boolean
}) => (
  <div className="flex items-center">
    <Controller
      name="attributes.locations_all"
      control={control}
      render={({ field }) => (
        <input
          type="checkbox"
          id="locations_all"
          checked={field.value || hasMultipleSystems}
          onChange={(e) => {
            if (!hasMultipleSystems && !isHighLevelRole) {
              field.onChange(e.target.checked)
            }
          }}
          onBlur={field.onBlur}
          ref={field.ref}
          disabled={hasMultipleSystems || isHighLevelRole}
          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      )}
    />
    <label
      htmlFor="locations_all"
      className={`ml-2 text-sm ${isHighLevelRole ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-400'}`}
    >
      Grant access to all
    </label>
  </div>
)
